<template>
  <div class="ma-3">
    <div class="title mb-3">
      Settings <v-icon>mdi-chevron-right</v-icon> Users
      <v-icon>mdi-chevron-right</v-icon>
      {{ isNew ? "New" : user.firstname + " " + user.lastname }}
    </div>

    <v-form
      ref="form"
      v-model="formValid"
      layout="column"
      layout-align="center center"
      autocomplete="off"
      @submit.prevent="save"
    >
      <div>
        <div class="title font-weight-light mb-3">
          User information
        </div>
        <v-text-field
          v-model="user.firstname"
          outline
          label="First name"
          class="field-width"
          :rules="[v => !!v || 'This field is required']"
        />
        <v-text-field
          v-model="user.lastname"
          outline
          label="Last name"
          class="field-width"
          :rules="[v => !!v || 'This field is required']"
        />
        <v-text-field
          v-model="user.email"
          outline
          label="E-mail"
          class="field-width"
          type="email"
        />
        <v-text-field
          v-model="user.phone"
          outline
          label="Phone number"
          class="field-width"
        />
        <v-text-field
          v-model="user.comment"
          outline
          label="Comment"
          class="field-width"
        />

        <div class="title font-weight-light mb-3">
          Login information
        </div>
        <v-text-field
          v-model="user.login"
          outline
          label="Login"
          class="field-width"
          :rules="[v => !!v || 'This field is required']"
        />
        <v-text-field
          v-model="user.password"
          outline
          label="Password"
          class="field-width"
          type="password"
          autocomplete="new-password"
        />

        <div class="title font-weight-light mb-3">
          Group information
        </div>
        <v-select
          v-model="user.groups"
          v-focus
          multiple
          outline
          label="Groups"
          :items="groups"
          item-value="id"
          item-text="name"
        />

        <v-select
          v-model="user.role"
          v-focus
          outline
          label="Role"
          :rules="[ (v) => !!v || 'This field is required' ]"
          :items="roles"
          @change="clearCTOptions()"
        />

        <template v-if="isCT">
          <v-select
            v-model="user.application"
            v-focus
            outline
            label="Application"
            :items="applications"
            :rules="[v => !!v || v === 0 || 'This field is required']"
          />
          <v-checkbox
            v-model="user.is_admin"
            label="This is an administrator (can access the administration)"
            :false-value="0"
            :true-value="1"
          />
          <v-checkbox
            v-model="user.is_team_responsible"
            label="This is a team responsible (allows indemnities' managment)"
            :false-value="0"
            :true-value="1"
            @change="clearResponsibleOf"
          />
          <v-select
            v-if="user.is_team_responsible"
            v-model="user.teams"
            v-focus
            multiple
            outline
            label="In charge of the teams"
            :items="teams"
            item-value="id"
            item-text="name"
            :rules="[v => !!v || 'This field is required' ]"
          />
        </template>
        <template v-if="isTra">
          <div class="title font-weight-light mb-3">
            Data for indemnities
          </div>
          <v-text-field
            v-model="user.matricule"
            outline
            label="N° de matricule"
            class=""
            :rules="[v => !!v || 'This field is required']"
          />
          <v-select
            v-model="user.country_id"
            v-focus
            outline
            clearable
            label="Country of residence"
            :rules="[v => !!v || 'This field is required' ]"
            :items="countries"
            item-value="iso"
            item-text="name"
          />
          <div v-if="user.country_id == 'lu'">
            <div layout="row">
              <v-menu
                v-model="menuVisible"
                :close-on-content-click="false"
                :nudge-width="200"
                :offset-y="true"
                max-height="300"
                open-on-focus
                transition="fade-transition"
              >
                <template #activator="{ on }">
                  <v-text-field
                    ref="searchField"
                    v-model="search"
                    label="Rechercher une rue luxembourgeoise"
                    :loading="loading"
                    clearable
                    hide-details
                    :disabled="ignore_lux_cities"
                    @input="onSearchInput"
                    @focus="menuVisible = true"
                    v-on="on"
                  />
                </template>
                <v-card flat>
                  <v-list v-if="streets.length > 0">
                    <v-list-tile
                      v-for="street in streets"
                      :key="street.id"
                      @click="onStreetSelected(street)"
                    >
                      <v-list-tile-content>
                        <v-list-tile-title>{{ street.name }}</v-list-tile-title>
                      </v-list-tile-content>
                    </v-list-tile>
                  </v-list>

                  <v-list v-else>
                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>Aucun résultat</v-list-tile-title>
                      </v-list-tile-content>
                    </v-list-tile>
                  </v-list>
                </v-card>
              </v-menu>
            </div>
            <div
              layout="row"
              style="margin-bottom: 20px"
            >
              <v-checkbox
                v-model="ignore_lux_cities"
                label="Je n'ai pas trouvé l'adresse"
                hide-details
                @change="onChangeIgnoreLuxCities"
              />
            </div>
          </div>
          <div class="one-row">
            <v-text-field
              v-model="user.streetnumber"
              outline
              label="Street number"
              class=" w200 mr-1"
              :rules="[v => !!v || 'This field is required']"
            />
            <v-text-field
              v-model="user.streetname"
              outline
              label="Street name"
              class=" grow"
              :rules="[v => !!v || 'This field is required']"
              :readonly="!ignore_lux_cities"
            />
          </div>
          <div class="one-row">
            <v-text-field
              v-model="user.postalcode"
              outline
              label="Postal code"
              class="w200 mr-1"
              :rules="[v => !!v || 'This field is required']"
              :readonly="!ignore_lux_cities"
            />
            <v-text-field
              v-model="user.city"
              outline
              label="City"
              class="grow"
              :rules="[v => !!v || 'This field is required']"
              :readonly="!ignore_lux_cities"
            />
          </div>
          <div class="one-row">
            <v-checkbox
              v-model="user.is_state_official"
              label="This trainer/AS is a state official"
              :false-value="0"
              :true-value="1"
              class="mr-3"
            />
            <v-checkbox
              v-model="user.is_independent"
              label="This trainer/AS is independent"
              :false-value="0"
              :true-value="1"
            />
          </div>
          <div class="one-row">
            <v-text-field
              v-model="user.iban"
              outline
              label="IBAN"
              class="grow mr-1"
              :rules="[v => !!v || 'This field is required']"
            />
            <v-text-field
              v-model="user.bic"
              outline
              label="BIC"
              class="w200"
              :rules="[v => !!v || 'This field is required']"
            />
          </div>
        </template>
      </div>

      <v-btn
        depressed
        color="primary"
        @click="save"
      >
        Save
      </v-btn>
      <router-link
        :to="{ name: 'admin-settings-users-index' }"
        class="grey--text"
      >
        Cancel
      </router-link>
    </v-form>
  </div>
</template>

<script>
import Params from "@/api/params";
import Consts from "@/consts";
import GlobalMixin from "@/mixins/global";

export default {
  mixins: [GlobalMixin],
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from.name;
    });
  },
  props: {
    value: {
      type: Object,
      required: false,
      default: () => {}
    }
  },
  data() {
    return {
      formValid: false,
      groups: [],
      teams: [],
      prevRoute: null,
      roles: [
          { text: 'Coordination', value: Consts.COORDINATION },
          { text: 'Trainer / Animator', value: Consts.TRAINER },
      ],
      user: {
        firstname: "",
        lastname: "",
        email: "",
        phone: "",
        comment: "",
        login: "",
        password: "",
        groups: [],
        role: null,
        application: null,
        is_admin: false,
        is_team_responsible: false,
        teams: [],
        matricule: '',
        country_id: '',
        streetnumber: '',
        streetname: '',
        postalcode: '',
        city: '',
        city_id: null,
        street_id: null,
        is_state_official: false,
        is_independent: false,
        iban: '',
        bic: ''
      },
      countries: [],
      streets: [],
      menuVisible: false,
      search: '',
      loading: false,
      ignore_lux_cities: true,
      searchTimeout: null
    };
  },
  computed: {
    isNew() {
      return this.$route.params.id === "new";
    },
    isTra() {
      if(this.user.role) {
        return this.user.role == Consts.TRAINER
      }
      return false;
    },
    isCT() {
      if(this.user.role) {
        return this.user.role == Consts.COORDINATION
      }
      return false;
    }
  },
  mounted() {
    const prGroups = Params.getList("groups", { is_enabled: 1, sortBy: "name", withoutIncludes: 1 })
    const prTeams = Params.getList("teams", { is_enabled: 1, sortBy: "name"})
    const prCountries = Params.getList("countries", {sortBy: "name"})
    Promise.all([prGroups, prTeams, prCountries]).then(res => {
      this.groups = res[0].data;
      this.teams = res[1].data;
      this.countries = res[2].data;
      if(this.$route.params.id && this.$route.params.id != 'new') {
        this.fetch()
      }
    })

  },
  methods: {
    fetch() {
      Params.get("users", this.$route.params.id).then(res => {
        this.user = res.data;

        this.ignore_lux_cities = this.user.country_id != 'lu' || ! this.user.street_id

        if (this.user) {
          if(this.user.groups && this.user.groups.length > 0){
            for (let i = 0; i < this.user.groups.length; i++) { //petite transformation des données pour n'avoir que le rôle
              this.user.groups[i] = this.user.groups[i].id
            }
          }
          if(this.user.teams && this.user.teams.length > 0){
            for (let i = 0; i < this.user.teams.length; i++) { 
              this.user.teams[i] = this.user.teams[i].id
            }
          }
        }
      });
    },
    save() {
      if (!this.$refs.form.validate()) {
        return;
      }

      if (this.isNew) {
        Params.create("users", this.user).then(() => {
          this.$router.push({
            name:
              this.prevRoute !== null
                ? this.prevRoute
                : "admin-settings-users-index"
          });
          this.$snotify.success(
            `The user "${this.user.firstname} ${this.user.lastname}" has been created`
          );
        });
      } else {
        Params.update("users", this.user.id, this.user).then(() => {
          this.$router.push({
            name:
              this.prevRoute !== null
                ? this.prevRoute
                : "admin-settings-users-index"
          });
          this.$snotify.success(
            `The user "${this.user.firstname} ${this.user.lastname}" has been updated`
          );
        });
      }
    },
    clearCTOptions() {
      if(! this.isCT) {
        this.user.application = null
        this.user.is_admin = false
        this.user.is_team_responsible = false
        this.user.teams = []
      }
    },
    clearResponsibleOf() {
      if(!this.user.is_team_responsible) {
        this.user.teams = []
      }
    },
    // ----------------- Pour la gestion de l'adresse -----------------
    onChangeCountry() {
      if(this.user.country_id == 'lu') {
        this.ignore_lux_cities = false
      }
      else {
        this.ignore_lux_cities = true
        this.user.city_id = null
        this.user.street_id = null
      }
    },
    onSearchInput(val) {
      if (!val || val.length < 3) {
        this.streets = [];
        this.menuVisible = false;
        return;
      }

      this.loading = true;
      this.menuVisible = true
      clearTimeout(this.searchTimeout) //debounce

      this.searchTimeout = setTimeout(() => {
        this.onSearchStreet(val)
      }, 500)
    },
    async onSearchStreet(search) {
      this.streets.splice(0)
      const {data} = await Params.getList("streets", {search})
      this.streets.push(...data)
      this.loading = false
    },
    onStreetSelected(street) {
      this.user.street_id = street.id ?? null
      if(this.user.street_id) {
        this.user.city_id = street.city_id
        this.user.streetname = street.streetname
        this.user.postalcode = street.postalcode
        this.user.city = street.cityname
      }
      else {
        this.user.city_id = null
        this.user.streetname = ""
        this.user.postalcode = ""
        this.user.city = null
      }

      this.search = street.name;
      this.menuVisible = false;
    },
    onChangeIgnoreLuxCities() {
      this.user.street_id = null
      this.user.city_id = null
    }
  }
};
</script>

<style scoped lang="sass">
  .one-row
    display: flex
    flex-direction: row
    flex-wrap: wrap

    .v-input
      flex: unset

    .grow
      flex-grow: 1

    .w350
      width: 350px
    .w200
      width: 200px
</style>
